import React, { useState, useEffect, useRef } from "react";
import { FaHeart, FaShareAlt, FaBookmark } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const PostStyle2 = ({ block, viewMode }) => {
  const [likes, setLikes] = useState(Number(block.likes || 0));
  const [liked, setLiked] = useState(() => JSON.parse(localStorage.getItem(`liked_${block.id}`)) || false);
  const [saves, setSaves] = useState(Number(block.saves || 0));
  const [saved, setSaved] = useState(() => JSON.parse(localStorage.getItem(`saved_${block.id}`)) || false);
  const [shares, setShares] = useState(Number(block.shares || 0));
  const [views, setViews] = useState(Number(block.views || 0));
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [loaded, setLoaded] = useState(false); // For lazy loading
  const [hover, setHover] = useState("no"); // State to control hover logic (default to "no")
  const postRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (liked) {
      localStorage.setItem(`liked_${block.id}`, JSON.stringify(liked));
    } else {
      localStorage.removeItem(`liked_${block.id}`);
    }

    if (saved) {
      localStorage.setItem(`saved_${block.id}`, JSON.stringify(saved));
    } else {
      localStorage.removeItem(`saved_${block.id}`);
    }
  }, [liked, saved]);

  useEffect(() => {
    // Only set up observer if video exists
    if (block.video_path) {
      if (hover === "yes") {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                setTimeout(() => {
                  setLoaded(true);
                }, 600);
              }
            });
          },
          { threshold: 0.5 }
        );

        if (postRef.current) {
          observer.observe(postRef.current);
        }

        return () => {
          if (postRef.current) {
            observer.unobserve(postRef.current);
          }
        };
      } else {
        setLoaded(true);
      }
    }
  }, [hover, block.video_path]); // Add video_path to dependencies

  const handleInteraction = (postId, action, remove = false) => {
    fetch("https://xebrand.in/Backend/update_interaction.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ post_id: postId, action, remove }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          if (action === "like") setLikes(Number(data.count));
          if (action === "save") setSaves(Number(data.count));
          if (action === "share") setShares(Number(data.count));
          if (action === "view") setViews(Number(data.count));
        } else {
          console.error("API Error:", data.message);
        }
      })
      .catch((error) => console.error("Fetch Error:", error));
  };

  const handlePostClick = () => {
    if (!localStorage.getItem(`viewed_${block.id}`)) {
      localStorage.setItem(`viewed_${block.id}`, true);
      setViews((prev) => prev + 1);
      handleInteraction(block.id, "view");
    }
    navigate(`/Shopify-custom-sections/post-${block.slug}`, { state: { post: block } });
  };

  const handleLike = (e) => {
    e.stopPropagation();
    if (liked) {
      setLikes((prev) => Math.max(0, prev - 1));
      handleInteraction(block.id, "like", true);
    } else {
      setLikes((prev) => prev + 1);
      handleInteraction(block.id, "like");
    }
    setLiked((prev) => !prev);
  };

  const handleSave = (e) => {
    e.stopPropagation();
    if (saved) {
      setSaves((prev) => Math.max(0, prev - 1));
      handleInteraction(block.id, "save", true);
    } else {
      setSaves((prev) => prev + 1);
      handleInteraction(block.id, "save");
    }
    setSaved((prev) => !prev);
  };

  const handleShare = (e) => {
    e.stopPropagation();
    handleInteraction(block.id, "share");
    const shareUrl = `${window.location.origin}/Shopify-custom-sections/post-${block.slug}`;
    if (navigator.share) {
      navigator
        .share({
          title: block.name,
          text: `Check out this amazing post: ${block.name}`,
          url: shareUrl,
        })
        .then(() => console.log("Post shared successfully"))
        .catch((error) => console.error("Error sharing:", error));
    } else {
      setShareModalVisible(true);
    }
  };

  const isNew = () => {
    const createdDate = new Date(block.created_at);
    const currentDate = new Date();
    const diffInDays = Math.floor((currentDate - createdDate) / (1000 * 60 * 60 * 24));
    return diffInDays <= 2;
  };

  return (
    <>
 <div
        className={`post-style2-card ${!loaded && hover === "yes" ? "post-style2-skeleton" : ""}`}
        ref={postRef}
        onClick={handlePostClick}
        onMouseEnter={() => block.video_path && setHover("yes")}
        onMouseLeave={() => block.video_path && setHover("no")}
      >
        {!loaded && hover === "yes" ? (
          <div>
            <div className="post-style2-skeleton-header"></div>
            <div className="post-style2-skeleton-line long"></div>
            <div className="post-style2-skeleton-line medium"></div>
            <div className="post-style2-skeleton-line short"></div>
          </div>
        ) : (
          <div className="post-style2-media">
            {/* Render logic based on hover state */}
            {block.video_path ? (
              <>
                {hover === "no" ? (
                  <img
                    src={block.image_path || "/fallback-image.jpg"}
                    alt={block.name || "Unnamed Post"}
                    className="post-style2-image"
                  />
                ) : (
                  <video
                    src={block.video_path}
                    autoPlay
                    loop
                    muted
                    className="post-style2-video"
                  />
                )}
              </>
            ) : (
              <img
                src={block.image_path || "/fallback-image.jpg"}
                alt={block.name || "Unnamed Post"}
                className="post-style2-image"
              />
            )}

            {isNew() && (
              <div className="post-style2-tag">
                <span>New</span>
              </div>
            )}

            <div className="post-style2-icons">
              <button className="icon-button" onClick={handleLike}>
                <FaHeart className={`icon ${liked ? "liked" : ""}`} />
              </button>
              <button className="icon-button" onClick={handleShare}>
                <FaShareAlt className="icon" />
              </button>
              <button className="icon-button" onClick={handleSave}>
                <FaBookmark className={`icon ${saved ? "saved" : ""}`} />
              </button>
            </div>
          </div>
        )}
              </div>
        {viewMode === "Details" && (
          <div className="post-style2-viewcount">
            <span className="view-count-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12 8.25C9.92893 8.25 8.25 9.92893 8.25 12C8.25 14.0711 9.92893 15.75 12 15.75C14.0711 15.75 15.75 14.0711 15.75 12C15.75 9.92893 14.0711 8.25 12 8.25ZM9.75 12C9.75 10.7574 10.7574 9.75 12 9.75C13.2426 9.75 14.25 10.7574 14.25 12C14.25 13.2426 13.2426 14.25 12 14.25C10.7574 14.25 9.75 13.2426 9.75 12Z"
                  fill="#8b51ff"
                ></path>
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12 3.25C7.48587 3.25 4.44529 5.9542 2.68057 8.24686L2.64874 8.2882C2.24964 8.80653 1.88206 9.28392 1.63269 9.8484C1.36564 10.4529 1.25 11.1117 1.25 12C1.25 12.8883 1.36564 13.5471 1.63269 14.1516C1.88206 14.7161 2.24964 15.1935 2.64875 15.7118L2.68057 15.7531C4.44529 18.0458 7.48587 20.75 12 20.75C16.5141 20.75 19.5547 18.0458 21.3194 15.7531L21.3512 15.7118C21.7504 15.1935 22.1179 14.7161 22.3673 14.1516C22.6344 13.5471 22.75 12.8883 22.75 12C22.75 11.1117 22.6344 10.4529 22.3673 9.8484C22.1179 9.28391 21.7504 8.80652 21.3512 8.28818L21.3194 8.24686C19.5547 5.9542 16.5141 3.25 12 3.25ZM3.86922 9.1618C5.49864 7.04492 8.15036 4.75 12 4.75C15.8496 4.75 18.5014 7.04492 20.1308 9.1618C20.5694 9.73159 20.8263 10.0721 20.9952 10.4545C21.1532 10.812 21.25 11.2489 21.25 12C21.25 12.7511 21.1532 13.188 20.9952 13.5455C20.8263 13.9279 20.5694 14.2684 20.1308 14.8382C18.5014 16.9551 15.8496 19.25 12 19.25C8.15036 19.25 5.49864 16.9551 3.86922 14.8382C3.43064 14.2684 3.17374 13.9279 3.00476 13.5455C2.84684 13.188 2.75 12.7511 2.75 12C2.75 11.2489 2.84684 10.812 3.00476 10.4545C3.17374 10.0721 3.43063 9.73159 3.86922 9.1618Z"
                  fill="#8b51ff"
                ></path>
              </svg>
            </span>
            <span className="count-text">{views} Views</span>
          </div>
        )}
        {viewMode === "Details" && (
          <div className="post2-titel-wrap">
            <h3 className="">{block.name || "Untitled Post"}</h3>
          </div>
        )}

    </>
  );
};

export default PostStyle2;
